<script setup lang="ts">
import { useVModel } from "@vueuse/core";
import InputText from "primevue/inputtext";
import { defineEmits, reactive, toRefs } from "vue";
import "maz-ui/css/main.css";

import type { ProfileUpdate } from "@profile-service-types/index.ts";
import { useField } from "@tanstack/vue-form";
import Skeleton from "primevue/skeleton";
import type { ProfileUpdateFormApi } from "@/components/Account/types.ts";
import CustomPhoneNumber from "@/components/shared/CustomPhoneNumber/index.vue";

interface Props {
  editable: boolean;
  modelValue: ProfileUpdate;
  parentForm: ProfileUpdateFormApi;
  hasEmailUpdatePermission: boolean;
}

const props = defineProps<Props>();
const { editable, parentForm, hasEmailUpdatePermission } = toRefs(props);

type LocalState = object;
const localState = reactive<LocalState>({});

const emit = defineEmits(["update:modelValue"]);
const profile = useVModel(props, "modelValue", emit);

const preferredFirstNameField = useField({ name: "preferred_first_name", form: parentForm.value });
const phoneNumberField = useField({ name: "phone_number", form: parentForm.value });
const secondaryPhoneNumberField = useField({ name: "secondary_phone_number", form: parentForm.value });
const pronounsField = useField({ name: "pronouns", form: parentForm.value });
const emailField = useField({ name: "email", form: parentForm.value });
const secondaryEmailField = useField({ name: "secondary_email", form: parentForm.value });
</script>

<template>
  <div class="grid grid-cols-3 gap-y-2">
    <parentForm.Subscribe>
      <template v-slot="{ canSubmit, isSubmitting }">
        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block" for="legalFirstName">Legal First Name*</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div class="">{{ profile.legal_first_name }}</div>
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block" for="preferredFirstName">Preferred First Name</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.preferred_first_name }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <InputText id="preferredFirstName" class="w-full" type="text" v-model="profile.preferred_first_name"
                       @blur="preferredFirstNameField.api.handleBlur"
                       @update:model-value="preferredFirstNameField.api.handleChange"
                       :invalid="!preferredFirstNameField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!preferredFirstNameField.state.value.meta.isValid">{{
                preferredFirstNameField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}
            </div>
          </div>
          <Skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block" for="lastName">Last Name*</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div class="">{{ profile.last_name }}</div>
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Email*</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="editable && !isSubmitting && hasEmailUpdatePermission" class="w-full">
            <InputText id="email" class="w-full"
                       type="text"
                       v-model="profile.email"
                       @blur="emailField.api.handleBlur"
                       @update:model-value="(value) => emailField.api.handleChange(value as string)"
                       :invalid="!emailField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!emailField.state.value.meta.isValid">{{
                emailField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}
            </div>
          </div>
          <div v-else class="min-w-0 break-all">{{ profile.email }}</div>
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block" for="secondaryEmail">Alternative Email</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.secondary_email }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <InputText id="secondaryEmail" class="w-full"
                       type="text"
                       v-model="profile.secondary_email"
                       @blur="secondaryEmailField.api.handleBlur"
                       @update:model-value="secondaryEmailField.api.handleChange"
                       :invalid="!secondaryEmailField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!secondaryEmailField.state.value.meta.isValid">{{
                secondaryEmailField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}
            </div>
          </div>
          <Skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto" for="phoneNumber">Phone Number</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.phone_number }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <CustomPhoneNumber
              v-model="profile.phone_number"
              class="w-full"
              @blur="phoneNumberField.api.handleBlur"
              @change="({wholeNumber, nationalNumber}) => phoneNumberField.api.handleChange(nationalNumber ? wholeNumber ?? undefined : undefined)"
              :invalid="!phoneNumberField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!phoneNumberField.state.value.meta.isValid">{{
              phoneNumberField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
            }}</div>
          </div>
          <Skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto" for="secondaryPhoneNumber">Alternative Phone Number</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.secondary_phone_number }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <CustomPhoneNumber
              v-model="profile.secondary_phone_number"
              class="w-full"
              @blur="secondaryPhoneNumberField.api.handleBlur"
              @change="({wholeNumber, nationalNumber}) => secondaryPhoneNumberField.api.handleChange(nationalNumber ? wholeNumber ?? undefined : undefined)"
              :invalid="!secondaryPhoneNumberField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!secondaryPhoneNumberField.state.value.meta.isValid">{{
                secondaryPhoneNumberField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}</div>
          </div>
          <Skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block" for="pronouns">Pronouns</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.pronouns }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <InputText id="pronouns" class="w-full" type="text" v-model="profile.pronouns"
                       @blur="pronounsField.api.handleBlur"
                       @update:model-value="pronounsField.api.handleChange"
                       :invalid="!pronounsField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!pronounsField.state.value.meta.isValid">{{
                pronounsField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}
            </div>
          </div>
          <Skeleton v-else />
        </div>
      </template>
    </parentForm.Subscribe>
  </div>
</template>

<style scoped>
.profile-custom-phone {
  @apply w-full font-sans leading-none m-0 rounded-md text-surface-600 dark:text-surface-200 placeholder:text-surface-400 dark:placeholder:text-surface-500 bg-surface-0 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 hover:border-primary-500 dark:hover:border-primary-400 focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-500/50 dark:focus:ring-primary-400/50 focus:z-10 appearance-none transition-colors duration-200
}

.profile-custom-phone :deep(.m-phone-input) {
  @apply border-l border-surface-300 focus:border-surface-300 dark:border-surface-600 hover:border-primary-500 dark:hover:border-primary-400 focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-500/50 dark:focus:ring-primary-400/50 focus:z-10;
  z-index: 5;
}

.profile-custom-phone :deep(.maz-rounded) {
  @apply border-none;
}
</style>
